// Centralized validation and input sanitization for BRSR

export const BrsrValidator = {
  // Validation rules
  RULES: {
    LINK_NAME_MIN: 3,
    LINK_NAME_MAX: 100,
    LINK_CATEGORY_MIN: 2,
    LINK_CATEGORY_MAX: 50,
    FILE_NAME_MAX: 255,
    FILE_SIZE_MAX: 50 * 1024 * 1024, // 50MB
    ALLOWED_FILE_TYPES: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/plain",
      "image/jpeg",
      "image/png",
      "image/gif",
    ],
  },

  // Sanitize URL input
  sanitizeUrl: (url) => {
    if (!url) return "";
    
    // Remove leading/trailing whitespace
    url = url.trim();
    
    // Only allow http, https, ftp protocols
    const protocolRegex = /^(https?|ftp):\/\//i;
    if (!protocolRegex.test(url)) {
      url = "https://" + url;
    }
    
    return url;
  },

  // Validate URL format
  isValidUrl: (urlString) => {
    try {
      const url = new URL(urlString);
      // Check for valid protocol
      if (!["http:", "https:", "ftp:"].includes(url.protocol)) {
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  },

  // Validate link name
  validateLinkName: (name) => {
    const errors = [];
    
    if (!name || name.trim().length === 0) {
      errors.push("Link name is required");
    } else {
      if (name.length < BrsrValidator.RULES.LINK_NAME_MIN) {
        errors.push(`Link name must be at least ${BrsrValidator.RULES.LINK_NAME_MIN} characters`);
      }
      if (name.length > BrsrValidator.RULES.LINK_NAME_MAX) {
        errors.push(`Link name cannot exceed ${BrsrValidator.RULES.LINK_NAME_MAX} characters`);
      }
      
      // Check for XSS attempts
      if (BrsrValidator.containsXssPatterns(name)) {
        errors.push("Link name contains invalid characters");
      }
    }
    
    return errors;
  },

  // Validate link category
  validateLinkCategory: (category) => {
    const errors = [];
    
    if (!category || category.trim().length === 0) {
      errors.push("Category is required");
    } else {
      if (category.length < BrsrValidator.RULES.LINK_CATEGORY_MIN) {
        errors.push(`Category must be at least ${BrsrValidator.RULES.LINK_CATEGORY_MIN} characters`);
      }
      if (category.length > BrsrValidator.RULES.LINK_CATEGORY_MAX) {
        errors.push(`Category cannot exceed ${BrsrValidator.RULES.LINK_CATEGORY_MAX} characters`);
      }
    }
    
    return errors;
  },

  // Validate file upload
  validateFile: (file) => {
    const errors = [];
    
    if (!file) {
      errors.push("Please select a file");
      return errors;
    }
    
    // Check file size
    if (file.size > BrsrValidator.RULES.FILE_SIZE_MAX) {
      errors.push(`File size cannot exceed ${(BrsrValidator.RULES.FILE_SIZE_MAX / (1024 * 1024)).toFixed(0)}MB`);
    }
    
    // Check file type
    if (!BrsrValidator.RULES.ALLOWED_FILE_TYPES.includes(file.type)) {
      errors.push("File type not allowed. Please upload PDF, Word, Excel, text, or image files");
    }
    
    // Check file name
    if (file.name.length > BrsrValidator.RULES.FILE_NAME_MAX) {
      errors.push(`File name is too long (max ${BrsrValidator.RULES.FILE_NAME_MAX} characters)`);
    }
    
    return errors;
  },

  // Check for XSS patterns
  containsXssPatterns: (input) => {
    const xssPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe[^>]*>/gi,
      /<img[^>]*onerror[^>]*>/gi,
      /<svg[^>]*onload[^>]*>/gi,
    ];
    
    return xssPatterns.some(pattern => pattern.test(input));
  },

  // Sanitize text input
  sanitizeText: (text) => {
    if (!text) return "";
    
    return text
      .trim()
      .replace(/[<>]/g, "") // Remove < and >
      .replace(/javascript:/gi, "") // Remove javascript: protocol
      .substring(0, 1000); // Limit length
  },

  // Validate link data object
  validateLinkData: (linkData) => {
    const errors = {};
    
    // Validate name
    const nameErrors = BrsrValidator.validateLinkName(linkData.linkName);
    if (nameErrors.length > 0) {
      errors.linkName = nameErrors[0];
    }
    
    // Validate category
    const categoryErrors = BrsrValidator.validateLinkCategory(linkData.linkCategory);
    if (categoryErrors.length > 0) {
      errors.linkCategory = categoryErrors[0];
    }
    
    // Validate URL
    if (!linkData.url || linkData.url.trim().length === 0) {
      errors.url = "URL is required";
    } else if (!BrsrValidator.isValidUrl(linkData.url)) {
      errors.url = "Please enter a valid URL (http, https, or ftp)";
    }
    
    return errors;
  },

  // Validate file data object
  validateFileData: (fileData) => {
    const errors = {};
    
    // Validate name
    if (!fileData.fileName || fileData.fileName.trim().length === 0) {
      errors.fileName = "File name is required";
    }
    
    // Validate category
    const categoryErrors = BrsrValidator.validateLinkCategory(fileData.fileCategory);
    if (categoryErrors.length > 0) {
      errors.fileCategory = categoryErrors[0];
    }
    
    // Validate file
    const fileErrors = BrsrValidator.validateFile(fileData.file);
    if (fileErrors.length > 0) {
      errors.file = fileErrors[0];
    }
    
    return errors;
  },

  // Check for duplicate links
  isDuplicateLink: (newLink, existingLinks) => {
    return existingLinks.some(
      (link) =>
        link.url.toLowerCase() === newLink.url.toLowerCase() ||
        (link.linkName.toLowerCase() === newLink.linkName.toLowerCase() &&
          link.linkCategory === newLink.linkCategory)
    );
  },

  // Format file size for display
  formatFileSize: (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  },

  // Validate GUID format
  isValidGuid: (guid) => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(guid);
  },
};

export default BrsrValidator;
